import {
  ConnectionListResult,
  GridSortProps,
  PaginatedQueryWrapper,
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import {
  VirtualConnectionDownstreamDashboardsFilteredByTableQuery,
  VirtualConnectionDownstreamDashboardsFilteredByTableQuery_virtualConnections_tablesConnection_nodes_downstreamDashboardsConnection_nodes,
  VirtualConnectionDownstreamDashboardsFilteredByTableQueryVariables,
} from '../../../gen-src/queries/VirtualConnectionDownstreamDashboardsFilteredByTableQuery';
import {
  VirtualConnectionDownstreamDashboardsQuery,
  VirtualConnectionDownstreamDashboardsQuery_virtualConnections_downstreamDashboardsConnection_nodes,
  VirtualConnectionDownstreamDashboardsQueryVariables,
} from '../../../gen-src/queries/VirtualConnectionDownstreamDashboardsQuery';
import { DashboardSortOrder } from '../../../gen-src/queries/globalTypes';
import { VirtualConnectionDownstreamDashboardsFilteredByTableQueryGql } from '../../queries/VirtualConnectionDownstreamDashboardsFilteredByTableQueryGql';
import { VirtualConnectionDownstreamDashboardsQueryGql } from '../../queries/VirtualConnectionDownstreamDashboardsQueryGql';
import { getRowDataForDashboard, DashboardsDisplayGrid, DashboardsDisplayGridRowData } from '../sharedDisplayGrid/DashboardsDisplayGrid';
import { DashboardsValidSortFields } from '../sorting/sortProviders/DashboardsGridSortProvider';


type QueryVariables = VirtualConnectionDownstreamDashboardsQueryVariables;
type QueryResults = VirtualConnectionDownstreamDashboardsQuery;
type QueryResultListNode = VirtualConnectionDownstreamDashboardsQuery_virtualConnections_downstreamDashboardsConnection_nodes;
type GridRowData = DashboardsDisplayGridRowData;

type QueryVariablesWithFilter = VirtualConnectionDownstreamDashboardsFilteredByTableQueryVariables;
type QueryResultsWithFilter = VirtualConnectionDownstreamDashboardsFilteredByTableQuery;
type QueryResultListNodeWithFilter =
  VirtualConnectionDownstreamDashboardsFilteredByTableQuery_virtualConnections_tablesConnection_nodes_downstreamDashboardsConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.virtualConnections && resultData.virtualConnections[0] &&
    resultData.virtualConnections[0].downstreamDashboardsConnection;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const virtualConnection = resultData && resultData.virtualConnections && resultData.virtualConnections[0];
  const virtualConnectionTables = virtualConnection && virtualConnection.tablesConnection && virtualConnection.tablesConnection.nodes;
  const dashboard = virtualConnectionTables && virtualConnectionTables[0];
  return dashboard ? dashboard.downstreamDashboardsConnection : null;
}

export type VirtualConnectionDownstreamDashboardsQueryGridProps = GridSortProps<DashboardsValidSortFields> & {
  readonly vizportalUrlId: string;
  readonly filteredTableId: string | null;
  readonly rowCount: number | null;
};

export function VirtualConnectionDownstreamDashboardsQueryGrid(props: VirtualConnectionDownstreamDashboardsQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyDownstreamDashboardsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredDashboardsLineageGridMessage();

  return props.filteredTableId ? (
    // Query with Selected Dashboard as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionDownstreamDashboardsFilteredByTableQueryGql}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        selectedTableId: props.filteredTableId,
        orderBy: props.orderBy as DashboardSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForDashboard}
    >
      {({ rowStore }) =>
        <DashboardsDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      }
    </PaginatedQueryWrapper>
  ) : (
    // Query without Selection
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionDownstreamDashboardsQueryGql}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        orderBy: props.orderBy as DashboardSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={getRowDataForDashboard}
    >
      {({ rowStore }) =>
        <DashboardsDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
    </PaginatedQueryWrapper>
  );
}
